import React, { useState, useRef } from 'react';
import { 
  Box, 
  Button, 
  IconButton, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Alert
} from '@mui/material';
import { Mic, Stop, CheckCircleOutline } from '@mui/icons-material';

const VoiceRecorder = ({ onTranscriptionComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [transcription, setTranscription] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  const openRecordingDialog = () => {
    setDialogOpen(true);
    setError('');
    setTranscription('');
    setRecordingTime(0);
  };

  const closeRecordingDialog = () => {
    if (isRecording) {
      stopRecording();
    }
    setDialogOpen(false);
  };

  const startRecording = async () => {
    try {
      audioChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        processAudioData();
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Start recording timer
      timerRef.current = setInterval(() => {
        setRecordingTime((time) => time + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError(`Could not access microphone: ${err.message}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      
      // Stop all audio tracks
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const processAudioData = async () => {
    try {
      setIsProcessing(true);
      
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      // Create a proper File object from the Blob
      const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
      
      // Development environment only - In production, this should go through your backend API
      if (process.env.NODE_ENV === 'development') {
        try {
          const { default: OpenAI } = await import('openai');
          
          const openai = new OpenAI({
            apiKey: process.env.REACT_APP_OPENAI_API_KEY,
            dangerouslyAllowBrowser: true // Only for development
          });
          
          const transcriptionResponse = await openai.audio.transcriptions.create({
            file: audioFile,
            model: "whisper-1",
          });
          
          const text = transcriptionResponse.text;
          setTranscription(text);
          
        } catch (err) {
          console.error('Error transcribing audio:', err);
          setError(`Transcription failed: ${err.message}`);
        }
      } else {
        // In production, we would call our backend API
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('model', 'whisper-1');
        
        const response = await fetch('/api/transcribe-audio', {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Transcription request failed with status ${response.status}`);
        }
        
        const data = await response.json();
        setTranscription(data.text);
      }
      
    } catch (err) {
      console.error('Error processing audio:', err);
      setError(`Processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUseTranscription = () => {
    if (transcription && onTranscriptionComplete) {
      onTranscriptionComplete(transcription);
      closeRecordingDialog();
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <>
      <IconButton 
        color="primary" 
        onClick={openRecordingDialog}
        size="small"
      >
        <Mic />
      </IconButton>

      <Dialog 
        open={dialogOpen} 
        onClose={closeRecordingDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Voice Recording</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 3 
          }}>
            {isProcessing ? (
              <>
                <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
                <Typography variant="body1">
                  Transcribing your recording...
                </Typography>
              </>
            ) : isRecording ? (
              <>
                <Box sx={{ 
                  width: 100, 
                  height: 100, 
                  borderRadius: '50%', 
                  bgcolor: 'error.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  animation: 'pulse 1.5s infinite',
                  mb: 2,
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.7)'
                    },
                    '70%': {
                      boxShadow: '0 0 0 15px rgba(211, 47, 47, 0)'
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)'
                    }
                  }
                }}>
                  <Stop sx={{ color: 'white', fontSize: 40 }} />
                </Box>
                <Typography variant="h6">
                  Recording... {formatTime(recordingTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Click the recording button again to stop
                </Typography>
              </>
            ) : transcription ? (
              <>
                <CheckCircleOutline sx={{ color: 'success.main', fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Transcription Complete
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  maxHeight: 200,
                  overflowY: 'auto'
                }}>
                  <Typography variant="body1">
                    {transcription}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ 
                  width: 100, 
                  height: 100, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }} onClick={startRecording}>
                  <Mic sx={{ color: 'white', fontSize: 40 }} />
                </Box>
                <Typography variant="h6">
                  Click to Start Recording
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Speak clearly, and we'll transcribe your words
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {isRecording ? (
            <Button 
              variant="contained" 
              color="error" 
              onClick={stopRecording}
              startIcon={<Stop />}
              fullWidth
            >
              Stop Recording
            </Button>
          ) : transcription ? (
            <>
              <Button onClick={closeRecordingDialog} color="inherit">
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleUseTranscription}
                autoFocus
              >
                Use Transcription
              </Button>
            </>
          ) : (
            <Button onClick={closeRecordingDialog} color="inherit">
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceRecorder; 